import { destroy, flow, getParent, types as t } from 'mobx-state-tree';

import { fileNameFromPath } from '@utils/strings';
import { isValidUuid } from '@utils/validators';

import { getTextForRating, sortBy, transformApplication, updateMSTObject } from 'helpers';

import {
  ApplicationChat,
  ApplicationForm,
  ApplicationMessage,
  ApplicationQuestionMinimal,
  ApplicationReview,
  ApplicationTasklist,
  ApplicationUserRead,
} from 'models/applications';
import { Integration } from 'models/integrations';
import { Job } from 'models/jobs';
import { Language, LoadingState } from 'models/ui';
import { UserRef } from 'models/users';
import { withEnv } from 'models/withEnv';
const SSNRegistry = t.model('SSNRegistry', {
  ssn: t.string,
  name: t.string,
  gender: t.maybeNull(t.string),
  age: t.number,
  ageYearEnd: t.number,
  street: t.maybeNull(t.string),
  city: t.maybeNull(t.string),
  postalCode: t.maybeNull(t.string),
});

export const Attachment = t
  .model('Attachment', {
    attachment: t.maybeNull(t.string),
    attachmentOriginal: t.maybeNull(t.string),
    blob: t.optional(t.frozen(), null),
    id: t.identifierNumber,
    loaded: false,
    fileName: t.maybe(t.string),
    questionId: t.maybeNull(t.string),
    questionTitle: t.maybeNull(t.string),
    date: t.maybeNull(t.optional(t.string, '')),
    user: t.maybeNull(
      t.union(
        t.undefined,
        t.late(() => UserRef)
      )
    ),
  })
  .views(withEnv)
  .views((self) => ({
    get noPreview() {
      return self.attachment && self.loaded && self.blob === null;
    },
    get name() {
      return self.fileName;
    },
  }))
  .actions((self) => ({
    loadAttachment: flow(function* () {
      if (self.loaded) return;
      if (!self.attachment) {
        self.loaded = true;
        return;
      }

      const res = yield self.env.api.applications.loadBlob(self.attachment);
      if (res.status === 200) {
        self.blob = res.data;
      }
      self.loaded = true;
    }),
  }));

export const ApplicationTrigger = t
  .model('ApplicationTrigger', {
    id: t.maybeNull(t.number),
    name: t.string,
    status: t.string,
    ratio: t.maybeNull(t.number),
    order: t.maybeNull(t.number),
    children: t.maybeNull(t.array(t.late(() => ApplicationTrigger))),
  })
  .views(withEnv)
  .views((self) => ({
    get statusText() {
      return self.status === 'completed'
        ? self.env.t('onboarding.applicants.status.completed')
        : self.status === 'in_progress'
        ? self.env.t('onboarding.applicants.status.inprogress')
        : self.env.t('onboarding.applicants.status.notstarted');
    },
  }));

export const Application = t
  .model('Application', {
    chat: t.array(ApplicationChat),
    contentCleared: t.optional(t.boolean, false),
    coverLetter: t.maybeNull(t.optional(t.string, '')),
    coverLetterOriginal: t.maybeNull(t.optional(t.string, '')),
    cv: t.maybeNull(t.optional(t.string, '')),
    cvOriginal: t.maybeNull(t.optional(t.string, '')),
    date: t.optional(t.string, ''),
    email: t.maybeNull(t.optional(t.string, '')),
    followers: t.array(t.late(() => UserRef)),
    form: t.maybeNull(t.maybe(ApplicationForm)),
    id: t.identifierNumber,
    isHired: t.optional(t.boolean, false),
    isManuallyAdded: t.optional(t.boolean, false),
    isNotifiedOfRejection: t.optional(t.boolean, false),
    isRejected: t.optional(t.boolean, false),
    isRetracted: t.optional(t.boolean, false),
    isOnboarded: t.optional(t.boolean, false),
    status: t.optional(
      t.enumeration('ApplicationStatus', ['active', 'hired', 'rejected', 'retracted', '']),
      ''
    ),
    job: t.maybe(t.reference(t.late(() => Job))),
    language: t.maybeNull(Language),
    name: t.maybeNull(t.optional(t.string, '')),
    order: t.optional(t.number, 0),
    phone: t.maybeNull(t.string),
    questions: t.array(ApplicationQuestionMinimal),
    referredBy: t.maybeNull(
      t.union(
        t.undefined,
        t.late(() => UserRef)
      )
    ),
    hiredByUser: t.maybeNull(
      t.union(
        t.undefined,
        t.late(() => UserRef)
      )
    ),
    rejectedByUser: t.maybeNull(
      t.union(
        t.undefined,
        t.late(() => UserRef)
      )
    ),
    referredSite: t.maybeNull(t.optional(t.string, '')),
    assignedTo: t.maybeNull(
      t.union(
        t.undefined,
        t.late(() => UserRef)
      )
    ),
    reviews: t.array(ApplicationReview),
    averageReviews: '-',
    averageReviewsNumber: 0,
    ssn: t.maybeNull(t.optional(t.string, '')),
    ssnStatus: t.maybeNull(
      t.union(
        t.undefined,
        t.enumeration('SSN Status', [
          'error',
          'unverified',
          'verified',
          'keep_or_match',
          'ssn_only',
        ])
      )
    ),
    ssnRegistry: t.maybeNull(SSNRegistry),
    jobStageId: t.maybe(t.number),
    jobStageName: t.maybe(t.string),
    jobStageOrder: t.maybe(t.number),

    integrationsResults: t.maybeNull(t.frozen()),
    integrations: t.array(Integration),
    isHiredTriggers: t.optional(t.array(ApplicationTrigger), []),
    dateOfHire: t.maybeNull(t.optional(t.string, '')),
    dateOfRejection: t.maybeNull(t.optional(t.string, '')),
    tasklist: t.maybe(ApplicationTasklist),
    tasklistState: LoadingState,
    taskListItems: t.maybe(t.array(t.number)),
    messages: t.array(ApplicationMessage),

    numAttachments: 0,
    numMessages: 0,
    numNotes: 0,
    numReviews: 0,

    coverLetterBlob: t.optional(t.frozen(), null),
    coverLetterLoaded: false,
    coverLetterNoPreview: false,
    cvBlob: t.optional(t.frozen(), null),
    cvLoaded: false,
    cvNoPreview: false,
    attachments: t.array(Attachment),
    uploadingAttachment: false,
    loaded: false,
    userRead: t.maybeNull(ApplicationUserRead),
    picture: t.maybeNull(t.string),
  })
  .views(withEnv)
  .views((self) => ({
    get jobId() {
      return self.job.id;
    },
    get integrationsLabels() {
      let labels = [];
      if (self.integrationsResults) {
        if ('hireflix' in self.integrationsResults) {
          if (self.integrationsResults.hireflix.status === 'sent') {
            labels.push({
              variant: 'orange',
              text: self.env.t('integrations.hireflix.application.status.sent'),
            });
          } else if (self.integrationsResults.hireflix.status === 'completed') {
            labels.push({
              variant: 'orange',
              text: self.env.t('integrations.hireflix.application.status.toevaluate'),
            });
          } else if (self.integrationsResults.hireflix.status === 'finalist') {
            labels.push({
              variant: 'green',
              text: self.env.t('integrations.hireflix.application.status.preselected'),
            });
          } else if (self.integrationsResults.hireflix.status === 'archived') {
            labels.push({
              variant: 'red',
              text: self.env.t('integrations.hireflix.application.status.discarded'),
            });
          }
        }
        if ('adManager' in self.integrationsResults) {
          if (self.integrationsResults.email !== '') {
            labels.push({
              variant: 'green',
              text: self.env.t('integrations.admanager.labels.created'),
            });
          }
        }
      }
      return labels;
    },
    get attachMap() {
      return self.attachments.reduce((res, item) => {
        res[item.id] = item;
        return res;
      }, {});
    },
    get cvFileName() {
      if (!self.cvOriginal) return '';
      return fileNameFromPath(self.cvOriginal);
    },
    get coverLetterFileName() {
      if (!self.coverLetterOriginal) return '';
      return fileNameFromPath(self.coverLetterOriginal);
    },
    get noCvPreview() {
      return self.cv && self.cvLoaded && self.cvBlob === null;
    },
    get noCoverLetterPreview() {
      return self.coverLetter && self.coverLetterLoaded && self.coverLetterBlob === null;
    },
    get averageRating() {
      return self.averageReviewsNumber;
    },
    get averageRatingText() {
      const rating = getTextForRating(self.averageReviews);
      return rating !== '-' ? self.env.t(rating) : rating;
    },
    get averageRatingLetter() {
      return self.averageReviews;
    },
    get baseUrl() {
      return `/jobs/${self.job.id}/application/${self.id}`;
    },
    get detailUrl() {
      return `/jobs/${self.job.id}/application/${self.id}/details`;
    },
    get onboardingUrl() {
      return `/jobs/${self.job.id}/onboarding/${self.id}`;
    },
    get onboardingDetailsUrl() {
      return `/jobs/${self.job.id}/onboarding/${self.id}/details`;
    },
    get onboardingRewardsUrl() {
      return `/jobs/${self.job.id}/onboarding/${self.id}/rewards`;
    },
    get allFollowers() {
      return sortBy(self.job.followers.concat(self.followers), 'name', 'asc');
    },
    get formQuestions() {
      const langObj =
        self.job.languages.find((obj) => obj.language === self.env.lang) ||
        self.job.languages.find((obj) => ['en', 'is'].includes(obj.language)) ||
        self.job.languages[0];
      if (!langObj) return [];
      let questions = [];
      ((self.form ?? {}).sections ?? []).forEach((section) => {
        section.questions.forEach((question) => {
          const questionTitle = langObj.getTitleByLanguage(question.id);
          if (questionTitle) {
            question.setTitle(questionTitle);
          }
          if (question.type === 'multidropdown' || question.type === 'dropdown') {
            if (!question.value) {
              questions.push({ ...question });
              return;
            }
            const isQuestionDeleted = langObj.isQuestionDeleted(question.id);
            if (isQuestionDeleted) {
              questions.push({
                ...question,
                value: self.env.t('application.details.deletedquestion'),
                isDeleted: true,
              });
              return;
            }
            const slice = question.value.slice();
            let val = Array.isArray(slice) ? slice : [question.value];
            val = val.map((v) => {
              const option = question.options?.find((option) => option.value === v);
              if (option) {
                return option.display;
              }
              const displayFromJobForm = langObj.findOptionDisplayByValue(v);
              if (displayFromJobForm) {
                return displayFromJobForm;
              } else {
                if (isValidUuid(v)) {
                  return null;
                } else {
                  return v;
                }
              }
            });

            questions.push({ ...question, value: val });
          } else {
            questions.push(question);
          }
        });
      });
      return questions;
    },
  }))
  .actions((self) => ({
    setJob(job) {
      self.job = job;
    },
    setLoaded(loaded) {
      self.loaded = loaded;
    },
    edit: flow(function* (formData) {
      const res = yield self.env.api.applications.edit(self.id, formData);
      if (!res.error) {
        updateMSTObject(self, transformApplication(res.data));
        if (formData.has('cv')) {
          self.cvLoaded = false;
          self.loadCv();
        }
        if (formData.has('coverLetter')) {
          self.coverLetterLoaded = false;
          self.loadCoverLetter();
        }
        self.env.showSuccess(self.env.t('application.edit.success'), { subtitle: self.name });
      } else {
        self.env.showError(self.env.t('application.edit.error'));
      }
      return res;
    }),
    sendMessage: flow(function* (message) {
      const res = yield self.env.api.applications.sendMessage(self.id, { message });
      if (!res.error) {
        self.messages = res.data.messages;
        self.numMessages = res.data.messages.length;
        self.env.showSuccess(self.env.t('application.messages.send.success'));
        return true;
      } else {
        self.env.showError(self.env.t('application.messages.send.error'));
        return false;
      }
    }),
    updateSsn: flow(function* (ssn) {
      const res = yield self.env.api.applications.editSsn(self.id, ssn);
      if (!res.error) {
        self.ssn = ssn;
        self.ssnStatus = 'unverified';
        self.ssnRegistry = res.data.ssnRegistry;
        self.env.showSuccess(self.env.t('application.edit.ssn.success'));
        return true;
      }
      self.env.showError(self.env.t('application.edit.ssn.error'));
      return false;
    }),
    setApplicationFollower(user) {
      self.followers.push(user);
      user.followCandidate(self.id);
    },
    setAllUserRead(data) {
      self.userRead = data;
    },
    setUserRead(fieldName) {
      if (self.userRead && self.userRead[fieldName] !== true) {
        self.userRead[fieldName] = true;
        self.env.api.applications.read(self.id, { [fieldName]: true });
      }
    },
    setAssignedTo(user) {
      self.assignedTo = user || null;
      return true;
    },
    shallowRemoveFollower(user) {
      self.followers = self.followers.filter((f) => f.id !== user.id);
    },
    removeFollowerAccess: flow(function* (user) {
      const followers = self.followers
        .slice()
        .filter((f) => f.id !== user.id)
        .map((f) => f.id);
      const res = yield self.env.api.applications.setFollowers(self.id, followers);
      if (!res.error) {
        self.env.showSuccess(
          self.env.t('application.removefollower.success', user.name, self.name)
        );
        self.shallowRemoveFollower(user);
        user.unfollowCandidate(self.id);
        return true;
      } else {
        self.env.showError(self.env.t('application.removefollower.error'));
        return false;
      }
    }),
    removeTasklist: flow(function* () {
      const res = yield self.env.api.applications.removeTasklist(self.id);
      if (!res.error) {
        destroy(self.tasklist);
        self.env.showSuccess(self.env.t('application.removetasklist.success'));
        return true;
      }
      self.env.showError(self.env.t('application.removetasklist.error'));
      return false;
    }),
    changeTasklist: flow(function* (templateId) {
      self.tasklistState = 'loading';
      const res = yield self.env.api.applications.saveTasklist(self.id, templateId);
      if (!res.error) {
        /**
         * @Refactor Graceful MST migration - Remove after server trim PR is merged.
         */
        self.tasklist = {
          ...res.data,
          items: res.data.items.map((item) => ({
            ...item,
            checkedBy: item.checkedBy
              ? typeof item.checkedBy === 'object'
                ? item.checkedBy.id
                : item.checkedBy
              : null,
          })),
        };
        self.tasklistState = 'loaded';
        return true;
      }
      self.tasklistState = 'error';
      return false;
    }),
    loadTasklist: flow(function* () {
      if (self.tasklistState !== 'init') return;
      self.tasklistState = 'loading';

      const res = yield self.env.api.applications.loadTasks(self.id);
      if (res.error) {
        self.tasklistState = 'error';
      } else if (!res.data.items) {
        self.tasklistState = 'loaded';
      } else {
        /**
         * @Refactor Graceful MST migration - Remove after server trim PR is merged.
         */
        self.tasklist = {
          ...res.data,
          items: res.data.items.map((item) => ({
            ...item,
            checkedBy: item.checkedBy
              ? typeof item.checkedBy === 'object'
                ? item.checkedBy.id
                : item.checkedBy
              : null,
          })),
        };
        self.tasklistState = 'loaded';
      }
    }),
    removeNote: flow(function* (note) {
      const res = yield self.env.api.applications.removeNote(note.id);
      if (!res.error) {
        self.numNotes -= 1;
        self.env.showSuccess(self.env.t('application.removenote.success'));
        destroy(note);
        return true;
      } else {
        self.env.showError(self.env.t('application.removenote.error'));
        return false;
      }
    }),
    addNote: flow(function* (message, isAdminNote) {
      const res = yield self.env.api.applications.addNote({
        text: message,
        application: self.id,
        isAdminNote: isAdminNote,
      });
      if (!res.error) {
        self.numNotes += 1;
        self.env.showSuccess(self.env.t('application.addnote.success'));
        /**
         * @Refactor Graceful MST migration - Remove after server trim PR is merged.
         */
        self.chat.unshift({
          ...res.data,
          user: typeof res.data.user === 'object' ? res.data.user.id : res.data.user,
        });
        return true;
      } else {
        self.env.showError(self.env.t('application.addnote.error'));
        return false;
      }
    }),
    setReview: flow(function* (review, currentUserId) {
      const myReview = self.reviews.find((review) => review.user.id === currentUserId);

      if (myReview && myReview.doesThink === review) {
        const res = yield self.env.api.applications.addReview({
          doesThink: '',
          application: self.id,
        });
        if (!res.error) {
          self.numReviews -= 1;
          myReview.setRating('');
          self.env.showSuccess(self.env.t('application.resetreview.success'), {
            subtitle: self.name,
          });
        } else {
          self.env.showError(self.env.t('application.resetreview.error'));
        }
      } else {
        const res = yield self.env.api.applications.addReview({
          doesThink: review,
          application: self.id,
        });
        if (!res.error) {
          if (!myReview || myReview.doesThink === '') {
            self.numReviews += 1;
          }
          self.env.showSuccess(self.env.t('application.setreview.success'), {
            subtitle: self.name,
          });
          if (myReview) {
            myReview.setRating(review);
          } else {
            /**
             * @Refactor Graceful MST migration - Remove after server trim PR is merged.
             */
            self.reviews.push({
              ...res.data,
              user: typeof res.data.user === 'object' ? res.data.user.id : res.data.user,
            });
          }
        } else {
          self.env.showError(self.env.t('application.setreview.error'));
        }
      }
      self.load();
    }),
    load: flow(function* () {
      const res = yield self.env.api.applications.load(self.id);
      updateMSTObject(self, transformApplication(res.data));
      self.loaded = true;
      if (self.userRead) {
        self.userRead.newApplication = true;
      }
    }),
    setIntegrations(data) {
      data.forEach((item) => {
        const integration = self.integrations.find(({ triggerId }) => triggerId === item.triggerId);
        if (!integration) {
          self.integrations.push({ ...item, type: 'applicant_hired' });
        } else {
          integration.ratio = item.ratio;
        }
      });
    },
    goToDetail(state) {
      self.env.router.push(`/jobs/${self.job.id}/application/${self.id}/details`, state);
    },
    loadCv: flow(function* () {
      if (self.cvLoaded) return;
      if (!self.cv) {
        self.cvLoaded = true;
        return;
      }

      const res = yield self.env.api.applications.loadBlob(self.cv);
      if (res.status === 200) {
        self.cvBlob = res.data;
      }
      self.cvLoaded = true;
    }),
    loadCoverLetter: flow(function* () {
      if (self.coverLetterLoaded) return;
      if (!self.coverLetter) {
        self.coverLetterLoaded = true;
        return;
      }

      const res = yield self.env.api.applications.loadBlob(self.coverLetter);
      if (res.status === 200) {
        self.coverLetterBlob = res.data;
      }
      self.coverLetterLoaded = true;
    }),
    addAttachment: flow(function* (data) {
      self.uploadingAttachment = true;
      const res = yield self.env.api.applications.addAttachment(self.id, data);
      if (res.status === 201) {
        self.numAttachments += 1;
        self.attachments.unshift({
          attachment: res.data.attachment,
          attachmentOriginal: res.data.attachmentOriginal,
          fileName: res.data.fileName,
          id: res.data.id,
          user: res.data.userId,
          date: res.data.date,
        });
        self.env.showSuccess(self.env.t('application.attachments.add.success'));
      } else {
        self.env.showError(self.env.t('application.attachments.add.error'));
      }
      self.uploadingAttachment = false;
    }),
    removeAttachment: flow(function* (attachment) {
      const res = yield self.env.api.applications.removeAttachment(self.id, attachment.id);
      if (res.status === 204) {
        self.numAttachments -= 1;
        destroy(attachment);
        self.env.showSuccess(self.env.t('application.attachments.remove.success'));
      } else {
        self.env.showError(self.env.t('application.attachments.remove.error'));
      }
      return true;
    }),
    changeStage: flow(function* (stage, showModal) {
      if (self.jobStageId === stage) return;
      const oldStage = self.job.jobStages.find((s) => s.id === self.jobStageId);
      const newStage = self.job.jobStages.find((s) => s.id === stage);
      self.jobStageId = stage;
      self.jobStageName = newStage.name;
      self.jobStageOrder = newStage.order;
      oldStage.removeApplication(self.id);
      newStage.prependApplication(self.id);
      const res = yield self.env.api.applications.moveStage(self.id, newStage.id);
      if (!res.error) {
        self.env.showSuccess(
          self.env.t('application.changestage.success', self.name, self.jobStageName)
        );
        if (newStage.dynamicStage.length > 0) {
          // TODO: Can there be multiple?
          const dynamicConfig = newStage.dynamicStage[0];
          if (dynamicConfig.autoOpenModal === 'HIRE_APPLICANT') {
            if (!self.isHired) {
              showModal(dynamicConfig.autoOpenModal, {
                _container: {
                  autoWidth: false,
                  closeOnOverlayClickOrEsc: false,
                },
                applicationId: self.id,
                applicationName: self.name,
                jobId: self.jobId,
                openOnboardModal: true,
              });
            }
          } else {
            showModal(dynamicConfig.autoOpenModal, {
              _container: {
                autoWidth: false,
                closeOnOverlayClickOrEsc: false,
              },
              applicationId: self.id,
              applicationName: self.name,
              applicationAssignedToId: self.assignedTo?.id,
            });
          }
        }
      } else {
        newStage.removeApplication(self.id);
        oldStage.prependApplication(self.id);
        self.jobStageId = oldStage.id;
        self.env.showError(self.env.t('application.changestage.error'));
      }
    }),
    delete() {
      return getParent(self, 2).delete(self);
    },
    markOnboarded: flow(function* (state) {
      const res = yield self.env.api.applications.onboard(self.id, state);
      if (!res.error) {
        self.isOnboarded = state;
        self.env.showSuccess(
          self.env.t(
            state ? 'application.onboard.success' : 'application.unonboard.success',
            self.name
          )
        );
        return true;
      } else {
        self.env.showError(
          self.env.t(state ? 'application.onboard.error' : 'application.unonboard.error', self.name)
        );
        return false;
      }
    }),
    reject: flow(function* (markNotified = false, emailData) {
      const res = yield self.env.api.applications.reject(self.id, markNotified, emailData);
      if (!res.error) {
        let msg = self.env.t('application.reject.success');
        if (emailData) {
          msg = self.env.t('application.reject.success.rejection');
        }
        self.isRejected = true;
        self.status = 'rejected';
        updateMSTObject(self, transformApplication(res.data));
        if (markNotified) {
          self.isNotifiedOfRejection = true;
        }
        self.env.showSuccess(msg);
        return true;
      } else {
        self.env.showError(self.env.t('application.reject.error'));
        return false;
      }
    }),
    unReject: flow(function* () {
      const res = yield self.env.api.applications.unReject(self.id);
      if (!res.error) {
        self.env.showSuccess(self.env.t('application.unreject.success'));
        self.isRejected = false;
        self.isNotifiedOfRejection = false;
        self.rejectedByUser = null;
        self.status = 'active';
      } else {
        self.env.showError(self.env.t('application.unreject.error'));
      }
    }),
    keepName: flow(function* () {
      const res = yield self.env.api.applications.keepOrMatchName(self.id, 'keep');
      if (!res.error) {
        self.ssnStatus = 'ssn_only';
        self.env.showSuccess(self.env.t('application.keepname.success'));
      } else {
        self.env.showError(self.env.t('application.keepname.error'));
      }
    }),
    matchName: flow(function* () {
      const res = yield self.env.api.applications.keepOrMatchName(self.id, 'match');
      if (!res.error) {
        self.name = self.ssnRegistry.name;
        self.ssnStatus = 'verified';
        self.env.showSuccess(self.env.t('application.matchname.success'));
      } else {
        self.env.showError(self.env.t('application.matchname.error'));
      }
    }),
    verifySsn: flow(function* () {
      const res = yield self.env.api.applications.verifySsn(self.id);

      if (!res.error) {
        self.ssnStatus = res.data.ssnStatus;
        if (self.ssnStatus === 'verified') {
          self.env.showSuccess(self.env.t('application.verifyssn.success'));
        } else if (self.ssnStatus === 'keep_or_match') {
          self.env.showSuccess(self.env.t('application.verifyssn.keepormatch'));
        }
      } else {
        if (res.status === 400) {
          self.ssnStatus = 'error';
          self.env.showError(self.env.t('application.verifyssn.error.notfound'));
        } else {
          self.env.showError(self.env.t('application.verifyssn.error'));
        }
      }
    }),
    goToOnboarding() {
      self.env.router.push(`/jobs/${self.job.id}/onboarding/${self.id}`);
    },
    setName(name) {
      self.name = name;
    },
    setEmail(email) {
      self.email = email;
    },
    setPhone(phone) {
      self.phone = phone;
    },
    setSsn(ssn) {
      self.ssn = ssn;
    },
    setHired(hired) {
      self.isHired = hired;
    },
    setRejected(rejected) {
      self.isRejected = rejected;
    },
    setRetracted(retracted) {
      self.isRetracted = retracted;
    },
    setStatus(status) {
      self.status = status;
    },
    setNotifiedOfRejection(notified) {
      self.isNotifiedOfRejection = notified;
    },
    setStage(id, name) {
      self.jobStageId = id;
      self.jobStageName = name;
    },
  }));
